import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APImethods, APIResponse, RequestParams } from './http.model';
import { CALL_LOADING } from '../loading-interceptor/loading-interceptor.service';
import { LoadingKeys } from 'src/app/shared/components/loading/loading-constants';
import { getAuthStateSelect } from '../store/auth/auth.selector';
import { AuthState } from '../store/auth/auth.state';
import { MedicalRecordState } from 'src/app/features/medical-record/store/medical-record.state';
import { Store } from '@ngrx/store';
import { IUser } from '../models/user.model';

type Api = 'patient' | 'questionnaire'

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private userLogged: IUser | null = null;
  private subscription: Subscription;

  constructor(private http: HttpClient,
    private store: Store<MedicalRecordState>,) {

    this.subscription = this.store.select(getAuthStateSelect).subscribe((user: AuthState) => {
      this.userLogged = user.user;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private generateURL(apiversion: 1 | 2, endpoint: string, api: Api) {
    if (api === 'patient') {
      return `${environment.patientUrl}${apiversion === 2 ? 'V2/' : ''}${this.userLogged?.clinic.dataStore}/${endpoint}`;
    }
    else {
      return `${environment.questionnaireUrl}${apiversion === 2 ? 'V2/' : ''}${this.userLogged?.clinic.dataStore}/${endpoint}`;
    }
  }

  private makeRequest<T>(url: string, method: APImethods, body: Object = {}, params: RequestParams = {}, context?: HttpContext) {
    switch (method) {
      case 'get':
        return firstValueFrom(this.http.get<APIResponse<T>>(url, { context, params }))
      case 'post':
        return firstValueFrom(this.http.post<APIResponse<T>>(url, body, {
          context,
          params: {
            ...params
          }
        }))
      case 'put':
        return firstValueFrom(this.http.put<APIResponse<T>>(url, body, {
          context,
          params: {
            ...params
          }
        }))
      case 'delete':
        return firstValueFrom(this.http.delete<APIResponse<T>>(url, {
          context,
          params: {
            ...params
          }
        }))
      default:
        throw new Error('')
        break;
    }
  }

  async req<T>(endpoint: string, api: Api,
    { body, params, apiVersion, method, loaderKey }: {
      body?: Object,
      params?: { [params: string]: string | string[] },
      apiVersion: 1 | 2,
      method: APImethods,
      loaderKey?: LoadingKeys | null
    } = { apiVersion: 1, body: {}, params: {}, method: 'get', loaderKey: null }
    // v2 = false ,body = {} , params = {}
  ) {

    const context = loaderKey ? new HttpContext().set(CALL_LOADING, loaderKey) : undefined

    const url = this.generateURL(apiVersion, endpoint, api);
    const response = await this.makeRequest<T>(url, method, body, params, context);
    return {
      ...response,
      status: response.status.toLowerCase().trim() as 'ok' | 'notok'
    } as APIResponse<T>
  }

  async tempReq<T>(endpoint: string,
    { body, params, method }: {
      body?: Object,
      params?: { [params: string]: string },
      method: APImethods
    } = { body: {}, params: {}, method: 'get' }
  ) {

    const url = environment.patientUrl + endpoint;
    return this.makeRequest<T>(url, method, body, params);
  }

  async reqBackend<T>(endpoint: string,
    { body, params, method }: {
      body?: Object,
      params?: { [params: string]: string },
      method: APImethods
    } = { body: {}, params: {}, method: 'get' }
  ) {

    const url = environment.backendUrl + endpoint;
    return this.makeRequest<T>(url, method, body, params);
  }
}
